import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, ArrowRight, Send, Clock, Users, FileText, Smartphone, CheckCircle, Loader2, CalendarIcon, Mail, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const STEPS = [
  { id: 1, label: 'Odbiorcy', icon: Users },
  { id: 2, label: 'Tresc', icon: FileText },
  { id: 3, label: 'Harmonogram', icon: Clock },
  { id: 4, label: 'Bramka / SMTP', icon: Mail },
  { id: 5, label: 'Podsumowanie', icon: CheckCircle },
];

const SEGMENT_LABELS = { hot: 'Goracy (>12 pkt)', warm: 'Ciepli (3-12 pkt)', cold: 'Zimni (1-2 pkt)', dead: 'Martwi (<1 pkt)' };

export default function CampaignBuilderPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Data
  const [templates, setTemplates] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [smtpAccounts, setSmtpAccounts] = useState([]);
  const [segmentStats, setSegmentStats] = useState({});

  // Form
  const [name, setName] = useState('');
  const [targetMode, setTargetMode] = useState('all');
  const [targetGroup, setTargetGroup] = useState('all');
  const [phoneStatusFilter, setPhoneStatusFilter] = useState('');
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [savedSegments, setSavedSegments] = useState([]);
  const [savedSegmentId, setSavedSegmentId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [scheduleMode, setScheduleMode] = useState('immediate');
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [selectedGateways, setSelectedGateways] = useState([]);
  const [selectedSmtpIds, setSelectedSmtpIds] = useState([]);
  const [aiInstruction, setAiInstruction] = useState('');
  const [aiModifying, setAiModifying] = useState(false);
  const [modifiedBody, setModifiedBody] = useState(null);
  const [modifiedSubject, setModifiedSubject] = useState(null);
  const [fromNameOverride, setFromNameOverride] = useState('');
  const [footerName, setFooterName] = useState('');
  const [footerAddress, setFooterAddress] = useState('');
  const [productId, setProductId] = useState('');
  const [products, setProducts] = useState([]);
  const [spamChecking, setSpamChecking] = useState(false);
  const [spamResult, setSpamResult] = useState('');
  const [autoAntispam, setAutoAntispam] = useState(true);
  const [footers, setFooters] = useState([]);
  const [selectedFooterId, setSelectedFooterId] = useState('');
  const [availableGroups, setAvailableGroups] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/templates'),
      api.get('/gateways'),
      api.get('/contacts/segments/stats'),
      api.get('/admin/smtp-accounts').catch(() => ({ data: { items: [] } })),
      api.get('/products'),
      api.get('/email-footers').catch(() => ({ data: { items: [] } })),
      api.get('/contacts/groups').catch(() => ({ data: [] })),
      api.get('/segments', { params: { with_counts: true } }).catch(() => ({ data: { items: [] } })),
    ]).then(([t, g, s, smtp, p, ft, grp, seg]) => {
      setTemplates(t.data.items);
      setGateways(g.data.items);
      setSegmentStats(s.data);
      setSmtpAccounts((smtp.data.items || []).filter(a => a.is_active && a.is_mailing !== false && a.domain !== 'torr.pl'));
      setProducts(p.data.items || []);
      setFooters(ft.data.items || []);
      setAvailableGroups(grp.data || []);
      setSavedSegments(seg.data?.items || []);
      const defFooter = (ft.data.items || []).find(f => f.is_default);
      if (defFooter) setSelectedFooterId(defFooter.id);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const selectedTemplate = templates.find(t => t.id === templateId);
  const [groupCount, setGroupCount] = useState(0);

  // Fetch group count when targetGroup changes
  useEffect(() => {
    const fetchGroupCount = async () => {
      try {
        const params = { limit: 0 };
        if (targetGroup && targetGroup !== 'all') params.group = targetGroup;
        const r = await api.get('/contacts', { params });
        setGroupCount(r.data.total);
      } catch { setGroupCount(0); }
    };
    fetchGroupCount();
  }, [targetGroup]);

  const estimatedRecipients = groupCount || segmentStats.total || 0;

  const toggleSegment = (seg) => {
    setSelectedSegments(prev => prev.includes(seg) ? prev.filter(s => s !== seg) : [...prev, seg]);
  };

  const toggleGateway = (id) => {
    setSelectedGateways(prev => prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]);
  };

  const toggleSmtp = (id) => {
    setSelectedSmtpIds(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const aiModifyTemplate = async () => {
    if (!aiInstruction || !selectedTemplate) return;
    setAiModifying(true);
    try {
      const r = await api.post('/ai/modify-template', {
        body: modifiedBody !== null ? modifiedBody : (selectedTemplate?.body || ''),
        instruction: aiInstruction,
        type: selectedTemplate?.type || 'email'
      });
      setModifiedBody(r.data.modified_body);
      toast.success('AI zmodyfikowal szablon');
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad AI'); }
    finally { setAiModifying(false); }
  };

  const runSpamCheck = async () => {
    if (!selectedTemplate) return;
    setSpamChecking(true); setSpamResult('');
    try {
      const r = await api.post('/ai/spam-check', { body: modifiedBody !== null ? modifiedBody : (selectedTemplate?.body || ''), subject: selectedTemplate?.subject || '', type: selectedTemplate?.type || 'email' });
      setSpamResult(r.data.analysis);
      toast.success('Analiza spam zakonczona');
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setSpamChecking(false); }
  };

  const [aiFixingSpam, setAiFixingSpam] = useState(false);
  const [aiWriting, setAiWriting] = useState(false);

  const aiAutoFixSpam = async () => {
    if (!selectedTemplate) return;
    setAiFixingSpam(true);
    try {
      const r = await api.post('/ai/modify-template', {
        body: modifiedBody !== null ? modifiedBody : selectedTemplate?.body || "",
        instruction: 'Popraw tresc aby OMIJALA filtry antyspamowe. Usun slowa spamowe (darmowy, za darmo, okazja, kliknij tutaj, natychmiast, 100%, gwarantowany). Zmien ton na naturalny, osobisty. Usun nadmiar wykrzyknikow i wielkich liter. Zachowaj placeholdery {imie} {link} {nazwisko}. Nie dodawaj stopki.',
        type: selectedTemplate?.type || "email"
      });
      setModifiedBody(r.data.modified_body);
      // Also fix subject if email
      if (selectedTemplate?.type === 'email') {
        const r2 = await api.post('/ai/modify-template', {
          body: modifiedSubject || selectedTemplate?.subject || '',
          instruction: 'Popraw temat emaila aby omijal filtry spam. Krotki, osobisty ton, bez wielkich liter, bez wykrzyknikow, bez slow spamowych. Max 50 znakow.',
          type: 'email'
        });
        setModifiedSubject(r2.data.modified_body);
      }
      toast.success('AI poprawil tresc pod katem antyspamu');
      setSpamResult('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad AI'); }
    finally { setAiFixingSpam(false); }
  };

  const aiWriteFriendly = async () => {
    if (!selectedTemplate) return;
    const product = products.find(p => p.id === productId);
    const productInfo = product ? `Produkt: ${product.name}. URL: ${product.url}` : '';
    setAiWriting(true);
    try {
      const r = await api.post('/ai/modify-template', {
        body: modifiedBody !== null ? modifiedBody : selectedTemplate?.body || "",
        instruction: `Napisz CALKOWICIE nowa tresc jako wiadomosc od ZNAJOMEGO ktory SAM skorzystal z oferty i poleca. Pisz krotko (3-4 zdania) jak do kumpla. Np: "Sluchaj, ostatnio trafilem na taka strone i zamoiwilem... polecam sprawdzic {link}". NIE reklama, NIE marketing. ${productInfo}. Bez polskich znakow. Nie dodawaj stopki.`,
        type: selectedTemplate?.type || "email"
      });
      setModifiedBody(r.data.modified_body);
      if (selectedTemplate?.type === 'email') {
        const r2 = await api.post('/ai/modify-template', {
          body: modifiedSubject !== null ? modifiedSubject : selectedTemplate?.subject || '',
          instruction: 'Napisz temat jak od znajomego: "musisz to zobaczyc" lub "pamietasz jak gadalismy?" NIE reklamowy. Max 40 znakow. Bez polskich znakow.',
          type: 'email'
        });
        setModifiedSubject(r2.data.modified_body);
      }
      toast.success('AI napisal tresc w stylu znajomego');
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad AI'); }
    finally { setAiWriting(false); }
  };




  const canNext = () => {
    if (step === 1) return name.trim() && targetGroup;
    if (step === 2) return !!templateId;
    if (step === 3) return scheduleMode === 'immediate' || (scheduleDate && scheduleTime);
    if (step === 4) {
      const isEmail = selectedTemplate?.type === 'email';
      if (isEmail) return selectedSmtpIds.length > 0 || smtpAccounts.length === 0;
      return selectedGateways.length > 0 || gateways.length === 0;
    }
    return true;
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      let scheduled_at = null;
      if (scheduleMode === 'scheduled' && scheduleDate) {
        const d = new Date(scheduleDate);
        const [h, m] = scheduleTime.split(':');
        d.setHours(parseInt(h), parseInt(m), 0, 0);
        scheduled_at = d.toISOString();
      }
      const payload = {
        name,
        type: selectedTemplate?.type || 'sms',
        template_id: templateId,
        product_id: productId && productId !== 'none' ? productId : null,
        scheduled_at,
        send_config: {
          gateway_ids: selectedGateways,
          smtp_account_ids: selectedSmtpIds,
          target_mode: targetMode,
          target_segments: selectedSegments,
          target_group: targetGroup,
          segment_id: (targetMode === 'saved_segment' && savedSegmentId) ? savedSegmentId : null,
          phone_status_filter: phoneStatusFilter,
          from_name_override: fromNameOverride || '',
          footer_id: selectedFooterId || '',
          modified_body: modifiedBody !== null ? modifiedBody : (selectedTemplate?.body || ''),
          modified_subject: modifiedSubject !== null ? modifiedSubject : (selectedTemplate?.subject || ''),
          auto_antispam: autoAntispam,
        }
      };
      const res = await api.post('/campaigns', payload);
      const campaignId = res.data.id;

      if (scheduleMode === 'immediate') {
        await api.post(`/campaigns/${campaignId}/start`);
        toast.success('Kampania uruchomiona!');
      } else {
        await api.post(`/campaigns/${campaignId}/schedule`);
        toast.success('Kampania zaplanowana!');
      }
      navigate('/campaigns');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Blad tworzenia kampanii');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="space-y-6"><Skeleton className="h-8 w-64" /><Skeleton className="h-96" /></div>
  );

  return (
    <div className="space-y-6" data-testid="campaign-builder">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/campaigns')} data-testid="campaign-builder-back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Kreator kampanii</h1>
          <p className="text-sm text-muted-foreground">Krok {step} z 5</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2 flex-1">
            <button
              onClick={() => s.id < step && setStep(s.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors duration-150 w-full ${
                s.id === step ? 'bg-primary/10 text-primary border border-primary/20' :
                s.id < step ? 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] cursor-pointer' :
                'bg-muted text-muted-foreground'
              }`}
              data-testid={`step-${s.id}`}
            >
              <s.icon className="h-3.5 w-3.5 shrink-0" />
              <span className="hidden sm:inline">{s.label}</span>
            </button>
            {i < STEPS.length - 1 && <div className="w-4 h-px bg-border shrink-0" />}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <Card className="border-border/50 bg-card/80">
        <CardContent className="pt-6">

          {/* Step 1: Odbiorcy */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label className="text-base font-medium">Nazwa kampanii</Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="np. Promocja Letnia 2026" className="max-w-md" data-testid="builder-campaign-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-base font-medium">Wybór odbiorców</Label>
                <RadioGroup value={targetMode} onValueChange={setTargetMode} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="all" id="rm-all" />
                    <Label htmlFor="rm-all">Grupa kontaktów (wszystkie / wybrana grupa)</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="saved_segment" id="rm-seg" />
                    <Label htmlFor="rm-seg">Zapisany segment (Segment Builder)</Label>
                  </div>
                </RadioGroup>
              </div>

              {targetMode !== 'saved_segment' && (
                <div className="space-y-2">
                  <Label className="text-base font-medium">Grupa kontaktow</Label>
                  <Select value={targetGroup} onValueChange={setTargetGroup}>
                    <SelectTrigger className="max-w-md"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Wszystkie grupy</SelectItem>
                      {availableGroups.map(g => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {targetMode === 'saved_segment' && (
                <div className="space-y-2">
                  <Label className="text-base font-medium">Wybierz segment</Label>
                  <Select value={savedSegmentId} onValueChange={setSavedSegmentId}>
                    <SelectTrigger className="max-w-md"><SelectValue placeholder="wybierz segment" /></SelectTrigger>
                    <SelectContent>
                      {savedSegments.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} {s.contact_count >= 0 ? `(${s.contact_count})` : ''}
                        </SelectItem>
                      ))}
                      {savedSegments.length === 0 && <SelectItem disabled value="_none">Brak zapisanych segmentów — utwórz w /segments</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                <span className="text-sm text-muted-foreground">Szacowana liczba odbiorcow w grupie: </span>
                <span className="text-sm font-semibold font-mono" data-testid="estimated-recipients">{estimatedRecipients}</span>
                <span className="text-xs text-muted-foreground ml-2">
                  ({targetGroup === 'all' ? 'wszystkie kontakty' : `grupa: ${targetGroup}`})
                </span>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Filtr statusu numeru</Label>
                <Select value={phoneStatusFilter} onValueChange={setPhoneStatusFilter}>
                  <SelectTrigger><SelectValue placeholder="Wszystkie numery" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Wszystkie numery</SelectItem>
                    <SelectItem value="active">Tylko aktywne (zweryfikowane PING)</SelectItem>
                    <SelectItem value="inactive">Tylko nieaktywne (do ponownej weryfikacji)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 2: Tresc */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label className="text-base font-medium">Wybierz szablon</Label>
                <Select value={templateId} onValueChange={v => { setTemplateId(v); setModifiedBody(null); setModifiedSubject(null); }}>
                  <SelectTrigger className="max-w-md" data-testid="builder-template-select"><SelectValue placeholder="Wybierz szablon" /></SelectTrigger>
                  <SelectContent>
                    {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({t.type.toUpperCase()})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {selectedTemplate && (
                <div className="space-y-3">
                  {/* Editable subject (email) */}
                  {selectedTemplate?.type === 'email' && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Temat emaila</Label>
                      <Input value={modifiedSubject !== null ? modifiedSubject : (selectedTemplate?.subject || '')} 
                        onChange={e => setModifiedSubject(e.target.value)}
                        placeholder="Temat wiadomosci..." data-testid="builder-subject-edit" />
                    </div>
                  )}
                  {/* Editable body */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Tresc wiadomosci {modifiedBody ? '(edytowana)' : ''}</Label>
                    <textarea value={modifiedBody !== null ? modifiedBody : (selectedTemplate?.body || '')} 
                      onChange={e => setModifiedBody(e.target.value)}
                      rows={selectedTemplate?.type === 'sms' ? 4 : 10}
                      className="w-full rounded-lg border border-input bg-background/50 px-3 py-2 text-sm font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                      data-testid="builder-body-edit" />
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(selectedTemplate?.variables || []).map(v => (
                      <Badge key={v} variant="secondary" className="text-xs font-mono cursor-pointer" onClick={() => setModifiedBody(prev => (prev || selectedTemplate?.body || "") + `{${v}}`)}>{`{${v}}`}</Badge>
                    ))}
                    <Badge variant="secondary" className="text-xs font-mono cursor-pointer" onClick={() => setModifiedBody(prev => (prev || selectedTemplate?.body || "") + '{link}')}>{'link'}</Badge>
                    <Badge variant="secondary" className="text-xs font-mono cursor-pointer" onClick={() => setModifiedBody(prev => (prev || selectedTemplate?.body || "") + '{imie}')}>{'imie'}</Badge>
                  </div>
                  {selectedTemplate?.type === 'sms' && (
                    <p className="text-xs text-muted-foreground">Dlugosc: {(modifiedBody !== null ? modifiedBody : selectedTemplate?.body || "").length} znakow ({Math.ceil((modifiedBody !== null ? modifiedBody : selectedTemplate?.body || "").length / 160)} SMS)</p>
                  )}
                  <Separator />
                  {/* AI Tools row */}
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={runSpamCheck} disabled={spamChecking} data-testid="spam-check-btn">
                      {spamChecking ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                      Sprawdz spam
                    </Button>
                    <Button size="sm" onClick={aiAutoFixSpam} disabled={aiFixingSpam} className="bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success))]/90" data-testid="ai-fix-spam-btn">
                      {aiFixingSpam ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                      AI popraw antyspam
                    </Button>
                    <Button size="sm" onClick={aiWriteFriendly} disabled={aiWriting} className="bg-primary text-primary-foreground hover:bg-primary/90">
                      {aiWriting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                      AI napisz od znajomego
                    </Button>
                    {(modifiedBody !== null || modifiedSubject !== null) && (
                      <Button variant="ghost" size="sm" onClick={() => { setModifiedBody(null); setModifiedSubject(null); }}>Przywroc oryginal</Button>
                    )}
                  </div>
                  {spamResult && (
                    <div className="p-3 rounded-lg bg-[hsl(var(--warning))]/5 border border-[hsl(var(--warning))]/20 text-xs whitespace-pre-wrap max-h-48 overflow-y-auto">
                      {spamResult}
                    </div>
                  )}
                  {/* Auto-antispam toggle */}
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--success))]/5 border border-[hsl(var(--success))]/20">
                    <Checkbox checked={autoAntispam} onCheckedChange={setAutoAntispam} />
                    <div>
                      <p className="text-sm font-medium text-[hsl(var(--success))]">Auto-antispam przy wysylce</p>
                      <p className="text-[10px] text-muted-foreground">AI automatycznie poprawi tresc przed wyslaniem (zamieni slowa spamowe, zlagodzi CTA)</p>
                    </div>
                  </div>
                  <Separator />
                  {/* AI custom modify */}
                  <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                    <div className="flex gap-2">
                      <Input value={aiInstruction} onChange={e => setAiInstruction(e.target.value)}
                        placeholder="Polecenie AI: Dodaj emotikony, Zmien ton, Skroc..."
                        className="flex-1 text-sm" data-testid="ai-modify-instruction" />
                      <Button variant="outline" size="sm" onClick={aiModifyTemplate} disabled={aiModifying || !aiInstruction}>
                        {aiModifying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                        Modyfikuj
                      </Button>
                    </div>
                  </div>
                  {/* Product selection for {link} */}
                  <Separator />
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Produkt (dla linku afiliacyjnego)</Label>
                    <Select value={productId} onValueChange={setProductId}>
                      <SelectTrigger className="max-w-md"><SelectValue placeholder="Wybierz produkt (opcjonalnie)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Brak produktu</SelectItem>
                        {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Harmonogram */}
          {step === 3 && (
            <div className="space-y-6">
              <Label className="text-base font-medium">Kiedy wyslac?</Label>
              <RadioGroup value={scheduleMode} onValueChange={setScheduleMode} className="space-y-3">
                <div className="flex items-center gap-3 p-3 rounded-lg border border-border/50 hover:bg-muted/30 transition-colors duration-150">
                  <RadioGroupItem value="immediate" id="immediate" data-testid="schedule-immediate" />
                  <Label htmlFor="immediate" className="cursor-pointer">
                    <span className="font-medium">Natychmiast</span>
                    <p className="text-xs text-muted-foreground">Kampania rozpocznie sie od razu</p>
                  </Label>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border border-border/50 hover:bg-muted/30 transition-colors duration-150">
                  <RadioGroupItem value="scheduled" id="scheduled" data-testid="schedule-later" />
                  <Label htmlFor="scheduled" className="cursor-pointer">
                    <span className="font-medium">Zaplanuj na pozniej</span>
                    <p className="text-xs text-muted-foreground">Wybierz date i godzine</p>
                  </Label>
                </div>
              </RadioGroup>
              {scheduleMode === 'scheduled' && (
                <div className="flex items-start gap-4 ml-8">
                  <div className="space-y-2">
                    <Label>Data</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-48 justify-start font-normal" data-testid="schedule-date-trigger">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {scheduleDate ? format(scheduleDate, 'dd.MM.yyyy') : 'Wybierz date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={scheduleDate} onSelect={setScheduleDate} />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Godzina</Label>
                    <Input type="time" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} className="w-32" data-testid="schedule-time" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Bramka / SMTP */}
          {step === 4 && (
            <div className="space-y-6">
              {selectedTemplate?.type === 'email' ? (
                <>
                  <Label className="text-base font-medium">Wybierz skrzynki pocztowe do wysylki</Label>
                  <p className="text-xs text-muted-foreground">Zaznacz jedna lub wiecej skrzynek. System losowo rozlozzy wysylke miedzy nimi.</p>
                  {smtpAccounts.length === 0 ? (
                    <div className="p-6 text-center text-muted-foreground">
                      <Mail className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                      <p>Brak aktywnych skrzynek. Skonfiguruj w Domeny SMTP.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {smtpAccounts.map(acc => (
                        <div key={acc.id} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors duration-150 cursor-pointer ${
                          selectedSmtpIds.includes(acc.id) ? 'border-primary/50 bg-primary/5' : 'border-border/50 hover:bg-muted/30'
                        }`} onClick={() => toggleSmtp(acc.id)}>
                          <Checkbox checked={selectedSmtpIds.includes(acc.id)} onCheckedChange={() => toggleSmtp(acc.id)} />
                          <div className="flex-1">
                            <span className="font-medium text-sm">{acc.from_email || acc.smtp_username}</span>
                            <div className="flex items-center gap-2 mt-0.5">
                              <Badge variant="outline" className="text-xs">{acc.domain}</Badge>
                              <span className="text-[10px] text-muted-foreground">{acc.from_name}</span>
                            </div>
                          </div>
                          <div className="text-right text-xs text-muted-foreground">
                            <p>Limit: {acc.daily_limit || 500}/dzien</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <Separator />
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Nazwa nadawcy (widoczna u odbiorcy)</Label>
                    <Input value={fromNameOverride} onChange={e => setFromNameOverride(e.target.value)} placeholder="np. Zespol Promocji, Jan z Tejwaz" className="max-w-md" />
                    <Label className="text-sm font-medium mt-3">Stopka emaila</Label>
                    <Select value={selectedFooterId} onValueChange={setSelectedFooterId}>
                      <SelectTrigger className="max-w-md"><SelectValue placeholder="Wybierz szablon stopki" /></SelectTrigger>
                      <SelectContent>
                        {footers.map(f => <SelectItem key={f.id} value={f.id}>{f.name} ({f.company_name})</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {!footers.length && <p className="text-xs text-muted-foreground">Brak stopek. Utworz w Stopki emaili.</p>}
                  </div>
                </>
              ) : (
                <>
                  <Label className="text-base font-medium">Wybierz bramki SMS</Label>
                  {gateways.length === 0 ? (
                    <div className="p-6 text-center text-muted-foreground">
                      <Smartphone className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                      <p>Brak bramek. Dodaj w Bramki SMS.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {gateways.map(gw => (
                        <div key={gw.id} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors duration-150 cursor-pointer ${
                          selectedGateways.includes(gw.id) ? 'border-primary/50 bg-primary/5' : 'border-border/50 hover:bg-muted/30'
                        }`} onClick={() => toggleGateway(gw.id)}>
                          <Checkbox checked={selectedGateways.includes(gw.id)} onCheckedChange={() => toggleGateway(gw.id)} />
                          <div className="flex-1">
                            <span className="font-medium text-sm">{gw.name}</span>
                            <Badge variant="outline" className="text-xs ml-2">{gw.type === 'gsm_modem' ? 'Modem' : 'Telefon'}</Badge>
                          </div>
                          <span className="text-xs text-muted-foreground">{gw.limits?.max_per_day || 800}/dzien</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 5: Podsumowanie */}
          {step === 5 && (() => {
            const isEmail = selectedTemplate?.type === 'email';
            const finalBody = modifiedBody !== null ? modifiedBody : (selectedTemplate?.body || '');
            const finalSubject = modifiedSubject !== null ? modifiedSubject : (selectedTemplate?.subject || '');
            const selectedSmtpAccs = smtpAccounts.filter(a => selectedSmtpIds.includes(a.id));
            const selectedFooter = footers.find(f => f.id === selectedFooterId);
            const selectedProduct = products.find(p => p.id === productId);
            // Preview: replace placeholders with example data
            const previewBody = finalBody.replace(/\{imie\}/g, 'Jan').replace(/\{nazwisko\}/g, 'Kowalski').replace(/\{miasto\}/g, 'Warszawa').replace(/\{email\}/g, 'jan@example.pl').replace(/\{link\}/g, selectedProduct?.url || '#').replace(/\{produkt_nazwa\}/g, selectedProduct?.name || '').replace(/\{unsubscribe_link\}/g, '#');
            const previewSubject = finalSubject.replace(/\{imie\}/g, 'Jan').replace(/\{nazwisko\}/g, 'Kowalski').replace(/\{produkt_nazwa\}/g, selectedProduct?.name || '');
            return (
            <div className="space-y-4">
              <Label className="text-base font-medium">Podsumowanie kampanii</Label>

              {/* Parametry */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Nazwa kampanii</p>
                  <p className="text-sm font-medium mt-1">{name}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Typ</p>
                  <p className="text-sm font-medium mt-1">{isEmail ? 'Email' : 'SMS'}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Odbiorcy</p>
                  <p className="text-sm font-medium font-mono mt-1">{estimatedRecipients} kontaktow</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Harmonogram</p>
                  <p className="text-sm font-medium mt-1">{scheduleMode === 'immediate' ? 'Natychmiast' : `${scheduleDate ? format(scheduleDate, 'dd.MM.yyyy') : ''} ${scheduleTime}`}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Nadawca</p>
                  <p className="text-sm font-medium mt-1">{fromNameOverride || selectedSmtpAccs[0]?.from_name || '-'}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Produkt</p>
                  <p className="text-sm font-medium mt-1">{selectedProduct?.name || 'Brak'}</p>
                </div>
              </div>

              {/* Skrzynki / Bramki */}
              <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">{isEmail ? 'Skrzynki pocztowe' : 'Bramki SMS'}</p>
                <div className="flex flex-wrap gap-1.5">
                  {isEmail ? selectedSmtpAccs.map(a => <Badge key={a.id} variant="secondary" className="text-xs">{a.from_email || a.smtp_username}</Badge>)
                    : selectedGateways.map(id => { const gw = gateways.find(g => g.id === id); return <Badge key={id} variant="secondary" className="text-xs">{gw?.name || id}</Badge>; })}
                  {(isEmail ? selectedSmtpIds.length : selectedGateways.length) === 0 && <span className="text-xs text-muted-foreground">Auto</span>}
                </div>
              </div>

              {/* Stopka */}
              {isEmail && selectedFooter && (
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Stopka emaila</p>
                  <p className="text-xs">{selectedFooter.company_name} | {selectedFooter.address} {selectedFooter.nip ? `| NIP: ${selectedFooter.nip}` : ''}</p>
                  <p className="text-[10px] text-muted-foreground">{selectedFooter.phone} | {selectedFooter.email} | {selectedFooter.website}</p>
                </div>
              )}

              {/* Podgląd treści */}
              <Separator />
              <Label className="text-sm font-medium">Podglad wiadomosci</Label>
              {isEmail && (
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Temat</p>
                  <p className="text-sm font-medium">{previewSubject}</p>
                </div>
              )}
              <div className="rounded-lg border border-border/50 overflow-hidden">
                <div className="bg-white text-black p-5" style={{maxWidth:600, margin:'0 auto', fontFamily:'Inter,Arial,sans-serif', fontSize:14, lineHeight:1.6}}>
                  <div dangerouslySetInnerHTML={{ __html: previewBody }} />
                  {isEmail && selectedFooter && (
                    <div style={{borderTop:'1px solid #ddd', marginTop:24, paddingTop:12}}>
                      <p style={{fontSize:11, color:'#666'}}>{selectedFooter.company_name} | {selectedFooter.address} {selectedFooter.nip ? `| NIP: ${selectedFooter.nip}` : ''}</p>
                      <p style={{fontSize:11, color:'#888'}}>{[selectedFooter.phone && `Tel: ${selectedFooter.phone}`, selectedFooter.email, selectedFooter.website].filter(Boolean).join(' | ')}</p>
                      <p style={{fontSize:11, color:'#999', borderTop:'1px solid #eee', paddingTop:8, marginTop:8}}>Nie chcesz otrzymywac wiadomosci? <span style={{color:'#2db8b0', textDecoration:'underline'}}>Kliknij aby sie wypisac</span></p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="secondary" onClick={() => step > 1 ? setStep(step - 1) : navigate('/campaigns')} data-testid="builder-prev">
          <ArrowLeft className="h-4 w-4 mr-2" />{step > 1 ? 'Wstecz' : 'Anuluj'}
        </Button>
        {step < 5 ? (
          <Button onClick={() => setStep(step + 1)} disabled={!canNext()} data-testid="builder-next">
            Dalej<ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={saving} data-testid="builder-submit" className="min-w-40">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            {scheduleMode === 'immediate' ? 'Uruchom kampanie' : 'Zaplanuj kampanie'}
          </Button>
        )}
      </div>
    </div>
  );
}
